// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Layer colours, draw order and default visibility.
 *
 * Approximates KiCad's classic dark theme closely enough to be recognisable —
 * the point is that the user can look at this and see their board, not that it
 * matches KiCad pixel for pixel.
 *
 * `order` is the painter's order: lower numbers are drawn first, underneath.
 * Back layers sit below inner layers, which sit below front layers, with the
 * board outline on top of everything.
 */

export interface LayerStyle {
  color: string;
  order: number;
  visibleByDefault: boolean;
}

const STYLES: Record<string, LayerStyle> = {
  // --- copper, back to front ------------------------------------------------
  'B.Cu': { color: '#4d7fc4', order: 10, visibleByDefault: true },
  'In6.Cu': { color: '#ff8000', order: 11, visibleByDefault: true },
  'In5.Cu': { color: '#f2eda1', order: 12, visibleByDefault: true },
  'In4.Cu': { color: '#00c2c2', order: 13, visibleByDefault: true },
  'In3.Cu': { color: '#ff26e2', order: 14, visibleByDefault: true },
  'In2.Cu': { color: '#8ab73b', order: 15, visibleByDefault: true },
  'In1.Cu': { color: '#c2c200', order: 16, visibleByDefault: true },
  'F.Cu': { color: '#c83434', order: 20, visibleByDefault: true },

  // --- technical ------------------------------------------------------------
  'B.Adhes': { color: '#3545a8', order: 24, visibleByDefault: false },
  'F.Adhes': { color: '#7a5eb8', order: 25, visibleByDefault: false },
  'B.Paste': { color: '#8a8a8a', order: 26, visibleByDefault: false },
  'F.Paste': { color: '#34c8c8', order: 27, visibleByDefault: false },
  'B.Mask': { color: '#93cf3c', order: 28, visibleByDefault: false },
  'F.Mask': { color: '#e754c7', order: 29, visibleByDefault: false },

  'B.SilkS': { color: '#e8b2a7', order: 32, visibleByDefault: true },
  'F.SilkS': { color: '#f2eda1', order: 33, visibleByDefault: true },

  'B.Fab': { color: '#4b4bab', order: 36, visibleByDefault: false },
  'F.Fab': { color: '#af4b4b', order: 37, visibleByDefault: false },
  'B.CrtYd': { color: '#00eeff', order: 38, visibleByDefault: false },
  'F.CrtYd': { color: '#ff26e2', order: 39, visibleByDefault: false },

  // --- documentation --------------------------------------------------------
  'Dwgs.User': { color: '#0364d3', order: 44, visibleByDefault: false },
  'Cmts.User': { color: '#7fc9ff', order: 45, visibleByDefault: false },
  'Eco1.User': { color: '#008500', order: 46, visibleByDefault: false },
  'Eco2.User': { color: '#c2c200', order: 47, visibleByDefault: false },
  Margin: { color: '#d357d2', order: 48, visibleByDefault: false },

  // --- the outline, always on top ------------------------------------------
  'Edge.Cuts': { color: '#d0d2cd', order: 60, visibleByDefault: true },

  // --- pseudo-layers ---------------------------------------------------------
  // Vias sit above copper; holes are punched through them; unsupported content
  // is drawn last so it can never be hidden behind anything.
  '~vias': { color: '#cf8f3f', order: 21, visibleByDefault: true },
  '~holes': { color: '#14141a', order: 22, visibleByDefault: true },
  '~unsupported': { color: '#d24bd2', order: 70, visibleByDefault: true },
};

/**
 * Pseudo-layers. Vias and holes span the whole stack rather than belonging to
 * one layer, and unsupported content belongs to no layer at all — each gets its
 * own entry so it sorts into the paint order and can be toggled like anything
 * else.
 */
export const VIA_LAYER = '~vias';
export const HOLE_LAYER = '~holes';
export const UNSUPPORTED_LAYER = '~unsupported';

export const PSEUDO_LAYER_NAMES: Record<string, string> = {
  [VIA_LAYER]: 'Vias',
  [HOLE_LAYER]: 'Holes',
  [UNSUPPORTED_LAYER]: 'Not rendered',
};

/** Human-facing name for a layer, including the pseudo-layers. */
export function layerLabel(name: string): string {
  return PSEUDO_LAYER_NAMES[name] ?? name;
}

const USER_COLORS = ['#8ab73b', '#c2c200', '#00c2c2', '#ff26e2', '#ff8000', '#7fc9ff', '#af4b4b', '#0364d3', '#93cf3c'];

const FALLBACK: LayerStyle = { color: '#9a9aa8', order: 50, visibleByDefault: false };

export function layerStyle(name: string): LayerStyle {
  const known = STYLES[name];
  if (known) return known;

  // Inner copper layers beyond In6, e.g. In7.Cu on a deep stack.
  const inner = /^In(\d+)\.Cu$/.exec(name);
  if (inner) {
    const n = Number(inner[1]);
    return {
      color: USER_COLORS[n % USER_COLORS.length]!,
      // Between B.Cu and F.Cu, deeper numbers nearer the back.
      order: 10 + Math.min(9, n),
      visibleByDefault: true,
    };
  }

  const user = /^User\.(\d+)$/.exec(name);
  if (user) {
    const n = Number(user[1]);
    return { color: USER_COLORS[(n - 1) % USER_COLORS.length]!, order: 49, visibleByDefault: false };
  }

  return FALLBACK;
}

export function isCopper(name: string): boolean {
  return name.endsWith('.Cu');
}

/** Sort layer names into painter's order. */
export function sortLayers(names: readonly string[]): string[] {
  return [...names].sort((a, b) => layerStyle(a).order - layerStyle(b).order || (a < b ? -1 : 1));
}

/**
 * KiCad's Appearance-panel order for the technical and user layers, mirroring
 * `LSET::TechAndUserUIOrder()`: front and back paired, front first.
 */
const UI_TECH_ORDER: readonly string[] = [
  'F.Adhes',
  'B.Adhes',
  'F.Paste',
  'B.Paste',
  'F.SilkS',
  'B.SilkS',
  'F.Mask',
  'B.Mask',
  'Dwgs.User',
  'Cmts.User',
  'Eco1.User',
  'Eco2.User',
  'Edge.Cuts',
  'Margin',
  'F.CrtYd',
  'B.CrtYd',
  'F.Fab',
  'B.Fab',
];

const UI_PSEUDO_ORDER: readonly string[] = [VIA_LAYER, HOLE_LAYER, UNSUPPORTED_LAYER];

/**
 * Where a layer sits in KiCad's Appearance panel, reading top to bottom.
 *
 * This is deliberately *not* {@link LayerStyle.order}. Paint order runs bottom
 * of the board upwards, because that is what makes the picture come out right;
 * the panel runs front of the board downwards, because that is how someone
 * looking at their board thinks about it. Sorting the layer buttons by paint
 * order puts B.Cu first and F.Cu in the middle, which reads as upside down to
 * anyone who has used KiCad.
 *
 * The sequence follows `LSET::UIOrder()`: the copper stack front to back, then
 * the technical and user layers, then the object pseudo-layers, which KiCad
 * lists under Objects below the layers proper.
 */
export function layerUiRank(name: string): number {
  if (name === 'F.Cu') return 0;
  const inner = /^In(\d+)\.Cu$/.exec(name);
  if (inner) return Math.min(98, Number(inner[1]));
  if (name === 'B.Cu') return 99;

  const tech = UI_TECH_ORDER.indexOf(name);
  if (tech !== -1) return 100 + tech;

  const user = /^User\.(\d+)$/.exec(name);
  if (user) return 200 + Number(user[1]);

  const pseudo = UI_PSEUDO_ORDER.indexOf(name);
  if (pseudo !== -1) return 400 + pseudo;

  // Anything this table does not know about — a layer from a newer KiCad, say.
  // Listed after the layers it does know and before the pseudo-layers, so it is
  // visible rather than buried.
  return 300;
}

/** Sort layer names the way KiCad's Appearance panel lists them, top to bottom. */
export function sortLayersForUI(names: readonly string[]): string[] {
  return [...names].sort((a, b) => layerUiRank(a) - layerUiRank(b) || (a < b ? -1 : 1));
}

/** The layers a board actually uses, in draw order. */
export function usedLayers(names: readonly string[]): string[] {
  return sortLayers([...new Set(names)]);
}
